import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Params } from '@angular/router';
import { ActivatedRoute } from '@freelancer/activated-route';
import { Auth } from '@freelancer/auth';
import type { CCFormExtraPayload } from '@freelancer/billing';
import type {
  DatastoreCollection,
  WebsocketMessage,
} from '@freelancer/datastore';
import {
  Datastore,
  WebSocketService,
  filterWebsocketMessage,
} from '@freelancer/datastore';
import type {
  CreditCard,
  DepositPageVarsCollection,
  DepositPageVarsWorldpayDdc,
  ThreeDSecureCompletedData,
} from '@freelancer/datastore/collections';
import { RepetitiveSubscription } from '@freelancer/decorators';
import type {
  ResponseData,
  SuccessResponseData,
} from '@freelancer/freelancer-http';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import type {
  InAppBrowserEvent,
  InAppBrowserRef,
} from '@freelancer/in-app-browser';
import { InAppBrowser } from '@freelancer/in-app-browser';
import { Location } from '@freelancer/location';
import {
  OverlayTypes,
  PaymentsErrorType,
  PaymentsEventType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { PaymentsPopUpService } from '@freelancer/payments-popup';
import { PaymentsTrackingService } from '@freelancer/payments-tracking';
import { PaymentsUtils } from '@freelancer/payments-utils';
import { PayPalNativeCheckoutAuth } from '@freelancer/paypal-native-checkout-auth';
import { Pwa } from '@freelancer/pwa';
import { Tracking } from '@freelancer/tracking';
import { toNumber } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { BackendErrorTypes } from 'api-typings/error-distribution';
import type {
  DepositConfirmResponseApi,
  DepositRequestActionApi,
  DepositRequestApi,
  DepositRequestConfirmationApi,
  ReferenceDepositRequestApi,
  ThreedsBrowserInfoApi,
  ThreedsDataApi,
  ThreedsResponseDataApi,
} from 'api-typings/payments/payments';
import {
  DepositMethodApi,
  ThreedsCapabilityApi,
} from 'api-typings/payments/payments';
import type { Observable } from 'rxjs';
import { Subscription, firstValueFrom, fromEvent, merge } from 'rxjs';
import { filter, map, switchMap, take, timeout } from 'rxjs/operators';
import type {
  PreprocessMilestonesRequest,
  PreprocessMilestonesResponse,
  ThreeDSContext,
} from './deposits.model';
import { ThreeDSState } from './deposits.types';
/**
 * Form data item
 */
export interface FormDataItem {
  name: string;
  value: string;
}

/**
 * 3DS messages types posted from popup window
 */
enum ThreeDSMessageType {
  TRIGGER = 'breakTo3ds',
  RETURN = 'returnFrom3DS',
}

@UntilDestroy({ className: 'Deposits' })
@Injectable({
  providedIn: 'root',
})
export class Deposits {
  @RepetitiveSubscription()
  private inAppBrowserMessageSubscription?: Subscription;
  private browser: InAppBrowserRef;
  private threeDSPopUp: Window | undefined;
  private paymentPopUp: Window | undefined;

  /**
   * a unique id of the deposit so that we can ensure that if
   * someone calls creditCardDeposit multiple times
   * we only finish processing the most recent call
   */
  private depositReference?: number;
  constructor(
    private auth: Auth,
    private freelancerHttp: FreelancerHttp,
    private http: HttpClient,
    private paymentsMessaging: PaymentsMessagingService,
    private paymentsUtils: PaymentsUtils,
    private paypalNativeCheckoutAuth: PayPalNativeCheckoutAuth,
    private location: Location,
    private inAppBrowser: InAppBrowser,
    private pwa: Pwa,
    private popUpService: PaymentsPopUpService,
    private tracking: Tracking,
    private activatedRoute: ActivatedRoute,
    private datastore: Datastore,
    private webSocketService: WebSocketService,
    private paymentsTracking: PaymentsTrackingService,
  ) {}

  /**
   * Deposit request setup through API
   *
   * @param payload Post request payload for deposit API
   */
  deposit(
    payload: DepositRequestApi,
  ): Observable<ResponseData<DepositRequestActionApi, any>> {
    return this.auth.getUserId().pipe(
      switchMap(userId =>
        this.freelancerHttp.post<DepositRequestActionApi>(
          'payments/0.1/deposits',
          { ...payload, user_id: toNumber(userId) },
        ),
      ),
      take(1),
    );
  }

  /**
   * Deposit request confirm through API
   *
   * @param payload Post request payload for deposit API
   */
  depositUpdate(
    payload: DepositRequestConfirmationApi,
  ): Promise<
    ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >
  > {
    return firstValueFrom(
      this.auth.getUserId().pipe(
        switchMap(userId =>
          // Response is {status: 'success', requestId: ...}
          this.freelancerHttp.put<
            DepositConfirmResponseApi,
            BackendErrorTypes['deposit_confirm']
          >('payments/0.1/deposits', {
            ...payload,
            user_id: toNumber(userId),
          }),
        ),
        untilDestroyed(this),
      ),
    );
  }

  /**
   * Recurring payment through API
   *
   * @param depositMethod Recurring payment method
   * @param billingAgreementId Billing agreement ID for recurring payment
   * @param amount The amount for recurring payment
   * @param depositCurrencyId The currency ID for deposit
   * @param paymentCurrencyId The currency ID for recurring payment
   * @param threatMetrixSessionId
   * @param trackingToken The token for tracking purpose
   * @param cartId The ID for cart that need to be paid
   * @param creditCardGateway recurring payment gateway
   */
  authorization(
    depositMethod: DepositMethodApi,
    billingAgreementId: string,
    amount: number,
    depositCurrencyId: number,
    paymentCurrencyId: number,
    threatMetrixSessionId: string,
    trackingToken: string,
    cartId?: number,
    creditCardGateway?: string,
    flnBillingData?: string,
    threedsCapability?: ThreedsCapabilityApi,
    threedsResponseData?: ThreedsResponseDataApi,
    invoiceId?: number,
    threedsBrowserInfo?: ThreedsBrowserInfoApi,
  ): Promise<
    ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >
  > {
    return firstValueFrom(
      this.auth.getUserId().pipe(
        switchMap(userId => {
          const payload = this.getAuthorizationPayload(
            toNumber(userId),
            depositMethod,
            billingAgreementId,
            amount,
            depositCurrencyId,
            paymentCurrencyId,
            threatMetrixSessionId,
            trackingToken,
            creditCardGateway,
            cartId,
            flnBillingData,
            threedsCapability,
            threedsResponseData,
            invoiceId,
            threedsBrowserInfo,
          );
          // Response is {status: 'success', requestId: ...}
          return this.freelancerHttp.put<
            DepositConfirmResponseApi,
            BackendErrorTypes['deposit_confirm']
          >('payments/0.1/authorization', payload);
        }),
        untilDestroyed(this),
      ),
    );
  }

  /**
   * Populates the browser info fields in CCFormExtraPayload.
   * *Note* Adopted from https://github.com/Adyen/adyen-3ds2-js-utils
   *
   * @param extraPayLoad CCFormExtraPayload object to populate.
   */
  populateBrowserInfo(extraPayLoad: CCFormExtraPayload): CCFormExtraPayload {
    const screen = window && window.screen;
    const navigator = window && window.navigator;
    const language = navigator && navigator.language;

    return {
      ...extraPayLoad,
      browserScreenWidth: screen ? screen.width.toString() : '',
      browserScreenHeight: screen ? screen.height.toString() : '',
      browserColourDepth: screen ? screen.colorDepth.toString() : '',
      browserTZoneOffset: new Date().getTimezoneOffset().toString(),
      browserLanguage: language ? language.toString() : '',
      // eslint-disable-next-line deprecation/deprecation
      browserJavaEnabled: navigator?.javaEnabled()?.toString() ?? 'false',
      browserJScriptEnabled: 'true',
    };
  }

  /**
   * Generate browser info fields for 3DSv2
   * *Note* Adopted from https://github.com/Adyen/adyen-3ds2-js-utils
   */
  generateThreedsBrowserInfo(): ThreedsBrowserInfoApi {
    const screen = window && window.screen;
    const navigator = window && window.navigator;
    const language = navigator && navigator.language;

    return {
      screen_width: screen ? screen.width.toString() : '',
      screen_height: screen ? screen.height.toString() : '',
      colour_depth: screen ? screen.colorDepth.toString() : '',
      tzone_offset: new Date().getTimezoneOffset().toString(),
      language: language ? language.toString() : '',
      // eslint-disable-next-line deprecation/deprecation
      java_enabled: navigator?.javaEnabled()?.toString() ?? 'false',
      jscript_enabled: 'true',
    };
  }

  /**
   * Creates a ThreeDSContext object based
   * on the  received ThreedsDataApi.
   *
   * @param threeDSApiData 3DS data field in the response payload to the
   *        DepositConfirm API request.
   * @param currentCtx threeDSContext
   */
  createThreeDSContext(
    threeDSApiData: ThreedsDataApi,
    currentCtx?: ThreeDSContext,
  ): ThreeDSContext | undefined {
    const curState = currentCtx ? currentCtx.type : undefined;

    if (
      !threeDSApiData.threeds_state ||
      !threeDSApiData.invoice_id ||
      !threeDSApiData.credit_card_gateway
    ) {
      return;
    }

    switch (threeDSApiData.threeds_state) {
      case ThreeDSState.REDIRECT: {
        if (!curState || curState === ThreeDSState.IDENTIFY) {
          return {
            ccGateway: threeDSApiData.credit_card_gateway,
            invoiceId: threeDSApiData.invoice_id,
            fxTransId: threeDSApiData.fx_transaction_id,
            requestAmount: threeDSApiData.request_amount,
            type: ThreeDSState.REDIRECT,
          };
        }
        break;
      }

      case ThreeDSState.IDENTIFY: {
        if (!curState) {
          return {
            ccGateway: threeDSApiData.credit_card_gateway,
            invoiceId: threeDSApiData.invoice_id,
            fxTransId: threeDSApiData.fx_transaction_id,
            requestAmount: threeDSApiData.request_amount,
            type: ThreeDSState.IDENTIFY,
          };
        }
        break;
      }

      case ThreeDSState.CHALLENGE: {
        if (!curState || curState === ThreeDSState.IDENTIFY) {
          return {
            ccGateway: threeDSApiData.credit_card_gateway,
            invoiceId: threeDSApiData.invoice_id,
            fxTransId: threeDSApiData.fx_transaction_id,
            requestAmount: threeDSApiData.request_amount,
            type: ThreeDSState.CHALLENGE,
          };
        }
        break;
      }
      default:
    }
  }

  /**
   * Initial 3DS flow
   *
   * @param context ThreeDSContext
   */
  initiate3DSFlow(context: ThreeDSContext): void {
    this.redirectUserTo3dsWaypoint(context.invoiceId);
  }

  /**
   * Create 3DS context for different 3DS state
   *
   * @param context 3DS context data object
   */
  constructThreeDSResponseApiData(
    context: ThreeDSContext,
  ): ThreedsResponseDataApi {
    return {
      threeds_state: context.type,
      fx_transaction_id: context.fxTransId,
      request_amount: context.requestAmount,
    };
  }

  /**
   * Get authorization payload
   *
   * @param userId user ID for recurring payment
   * @param depositMethod Recurring payment method
   * @param billingAgreementId Billing agreement ID for recurring payment
   * @param amount The amount for recurring payment
   * @param depositCurrencyId The currency ID for deposit
   * @param paymentCurrencyId The currency ID for recurring payment
   * @param threatMetrixSessionId Metrix session ID for tracking purpose
   * @param trackingToken The token for tracking purpose
   * @param cartId The ID for cart that need to be paid
   * @param creditCardGateway Recurring payment gateway
   */
  private getAuthorizationPayload(
    userId: number,
    depositMethod: DepositMethodApi,
    billingAgreementId: string,
    amount: number,
    depositCurrencyId: number,
    paymentCurrencyId: number,
    threatMetrixSessionId: string,
    trackingToken: string,
    creditCardGateway?: string,
    cartId?: number,
    flnBillingData?: string,
    threedsCapability?: ThreedsCapabilityApi,
    threedsResponseData?: ThreedsResponseDataApi,
    invoiceId?: number,
    threedsBrowserInfo?: ThreedsBrowserInfoApi,
  ): ReferenceDepositRequestApi {
    return {
      user_id: userId,
      deposit_method: depositMethod,
      amount,
      currency_id: depositCurrencyId,
      tracking_token: trackingToken,
      recurring_order_id: billingAgreementId,
      threatmetrix_session: threatMetrixSessionId,
      issuing_currency_id: paymentCurrencyId,
      credit_card_gateway: creditCardGateway,
      cart_id: cartId,
      threeds_capability: threedsCapability,
      threeds_response_data: threedsResponseData,
      fln_billing_data: flnBillingData,
      invoice_id: invoiceId,
      threeds_browser_info: threedsBrowserInfo,
    };
  }

  /**
   * Direct user to GAF transit endpoint for 3DS.
   * The backend will handle the logic to redirect user to issuer's site
   * depending on the gateway and 3DS version.
   *
   * @param invoiceID number
   */
  private async redirectUserTo3dsWaypoint(invoiceID: number): Promise<void> {
    if (!this.isNative()) {
      if (!this.threeDSPopUp) {
        this.threeDSPopUp = await this.popUpService.openPopUp('threeDSPopup');
      }
      this.threeDSPopUp.location.href = `payments/deposit/threeds-request-redirect.php?invoiceID=${invoiceID}`;
    } else {
      this.browser = this.inAppBrowser.open(
        `payments/deposit/threeds-request-redirect.php?invoiceID=${invoiceID}`,
      );
      this.browser.onExit().then(() => {
        this.paymentsMessaging.pushEvent({
          eventType: PaymentsEventType.CLOSE_OVERLAY,
        });
      });
    }
  }

  /**
   * Generates confirmation url for deposit payload
   *
   * @param backUrl
   * @param enablePopUp
   */
  getConfirmUrl({
    backUrl,
    enablePopUp,
  }: {
    backUrl?: string;
    enablePopUp?: boolean;
  }): string {
    // Force popup if we're in the app
    const isPopup = enablePopUp ?? this.pwa.isNative();
    // TODO: T235225 - migrate confirm.php to webapp
    const url = `${this.location.origin}/payments/deposit/confirm.php`;
    const params = new URLSearchParams();
    if (isPopup) {
      params.append('popup', 'true');
    }
    if (backUrl) {
      params.append('back', backUrl);
    }
    return `${url}?${params.toString()}`;
  }

  /**
   * Make a deposit request to backend for all payment methods that need popup window
   *
   * @param payload Deposit payload for deposit API
   * @param enablePopup If we need show popup window or in-app-browser
   */
  async makeDeposit({
    payload,
    enablePopup,
  }: {
    payload: DepositRequestApi;
    enablePopup: boolean;
  }): Promise<void> {
    this.paymentsTracking.pushTrackingData({
      label: `depositAttempted-${payload.deposit_method}`,
      name: payload.deposit_method,
    });

    const shouldUseNewPayPalCheckout =
      (payload.deposit_method === DepositMethodApi.PAYPAL ||
        payload.deposit_method === DepositMethodApi.PAYPAL_REFERENCE) &&
      this.isUserEligibleForNewNativePayPalCheckout();

    const newPayload: DepositRequestApi = {
      ...payload,
      confirm_url: shouldUseNewPayPalCheckout
        ? `${payload.confirm_url}&platform=${this.pwa.getPlatform()}`
        : payload.confirm_url,
    };

    const res = await firstValueFrom(
      this.deposit(newPayload).pipe(untilDestroyed(this)),
    );
    if (res.status === 'error') {
      this.paymentsMessaging.pushError({
        errorType: PaymentsErrorType.ERROR,
        errorCode: res.errorCode,
        eventId: res.requestId,
      });
      return;
    }
    const { url } = res.result;
    if (!url) {
      this.paymentsMessaging.pushError({
        errorType: PaymentsErrorType.ERROR,
      });
      return;
    }
    if (this.pwa.isNative()) {
      if (shouldUseNewPayPalCheckout) {
        this.nativePayPalCheckoutAuth(url, payload.cart_id, res.requestId);
        return;
      }

      // In native app, open all methods with in-app browser
      this.browser = this.inAppBrowser.open(url);
      let isPending = false;
      this.inAppBrowserMessageSubscription = this.browser
        .onMessage()
        .subscribe(event => {
          const message = this.paymentsUtils.handleInterframeDepositResult(
            event.data,
          );
          if (message) {
            if (
              'paymentsStatus' in message &&
              message.paymentsStatus === PaymentsResultStatus.PENDING &&
              (res.result.deposit_method ===
                DepositMethodApi.PAYPAL_REFERENCE ||
                res.result.deposit_method === DepositMethodApi.PAYPAL)
            ) {
              this.paymentsMessaging.pushOverlay({
                titleText: $localize`Checkout Complete`,
                overlayType: OverlayTypes.PAYPAL_PENDING,
                cartId: payload.cart_id,
              });
              isPending = true;
            }
            this.paymentsMessaging.pushMessage(message);
          } else {
            this.handleError(res.requestId);
          }
          this.browser.close();
        });
      this.browser.onExit().then(() => {
        if (!isPending) {
          this.paymentsMessaging.pushEvent({
            eventType: PaymentsEventType.CLOSE_OVERLAY,
          });
        }
        if (this.inAppBrowserMessageSubscription) {
          this.inAppBrowserMessageSubscription.unsubscribe();
        }
      });
    } else if (enablePopup) {
      const logo = {
        src: this.paymentsUtils.getMethodLogoPath(payload.deposit_method),
        alt: `${payload.deposit_method} logo`,
      };
      let subscription: Subscription | undefined;
      let isPopupVisible = false;

      if (payload.deposit_method === DepositMethodApi.PAYPAL) {
        try {
          this.paymentPopUp = await this.popUpService.openPopUp(
            `depositPopUp-${payload.deposit_method}`,
          );

          isPopupVisible = true;
          subscription = this.setupRedirectionAndListen(
            this.paymentPopUp,
            res,
            payload.deposit_method,
            payload.cart_id,
          );
          this.paymentsMessaging.pushOverlay({
            brand: logo,
            onClose: () => {
              if (!this.paymentPopUp) {
                return;
              }
              this.popUpService.closePopUp();
              this.paymentPopUp = undefined;
              subscription?.unsubscribe();
            },
            overlayType: OverlayTypes.EXTERNAL_POPUP,
            method: payload.deposit_method,
          });
        } catch (e) {
          isPopupVisible = false;
        }
      }

      if (!isPopupVisible) {
        this.paymentsMessaging.pushOverlay({
          brand: logo,
          ctaAction: async () => {
            if (this.paymentPopUp) {
              this.paymentPopUp.focus();
            } else {
              this.paymentPopUp = await this.popUpService.openPopUp(
                `depositPopUp-${payload.deposit_method}`,
              );
              subscription = this.setupRedirectionAndListen(
                this.paymentPopUp,
                res,
                payload.deposit_method,
                payload.cart_id,
              );
            }
          },
          onClose: () => {
            if (!this.paymentPopUp) {
              return;
            }
            this.popUpService.closePopUp();
            this.paymentPopUp = undefined;
            subscription?.unsubscribe();
          },
          overlayType: OverlayTypes.EXTERNAL_POPUP,
          method: payload.deposit_method,
        });
      }
      // move Popup logic to this block T218849
    } else if (url.startsWith('https')) {
      this.location.redirect(url);
    } else {
      this.location.navigateByUrl(url);
    }
  }

  async recurringPayment({
    depositMethod,
    billingAgreementId,
    amount,
    depositCurrencyId,
    paymentCurrencyId,
    threatMetrixSessionId,
    trackingToken,
    cartId,
    creditCard,
    billingData,
    worldpayContext,
  }: {
    depositMethod: DepositMethodApi;
    billingAgreementId: string;
    amount: number;
    depositCurrencyId: number;
    paymentCurrencyId: number;
    threatMetrixSessionId: string;
    trackingToken: string;
    cartId?: number;
    creditCard?: CreditCard;
    billingData?: string;
    worldpayContext?: DepositPageVarsWorldpayDdc;
  }): Promise<void> {
    const depositReference = Date.now();
    this.depositReference = depositReference;

    const isWorldpay = creditCard?.gateway === 'worldpay';
    let doDdc = false;
    let cardinalCommerceSessionId = '';
    // T270846 - Disabling DDC for iOS Capacitor until fixed
    if (isWorldpay && worldpayContext && this.pwa.getPlatform() !== 'ios') {
      if (!creditCard?.bin) {
        this.handleError(undefined, PaymentsErrorType.ERROR_UNDEFINED_DATA);
        return;
      }
      doDdc = true;
      cardinalCommerceSessionId = await this.initWorldpayDdc(
        worldpayContext,
        creditCard?.bin.toString(),
      );
    }

    let response = await this.authorization(
      depositMethod,
      billingAgreementId,
      amount,
      depositCurrencyId,
      paymentCurrencyId,
      threatMetrixSessionId,
      trackingToken,
      cartId,
      creditCard?.gateway,
      billingData,
      ThreedsCapabilityApi.VER1,
      doDdc
        ? {
            threeds_state: ThreeDSState.DO_DDC,
            ddc_session_id: cardinalCommerceSessionId,
          }
        : undefined,
      undefined,
      this.generateThreedsBrowserInfo(),
    );
    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode, depositMethod);
      return;
    }
    if (this.handleThreeDSResponse(response, depositMethod)) {
      return;
    }

    // 3DS is triggered
    if (!response.result.threeds_data) {
      return this.handleError(response.requestId);
    }

    const threeDSCtx = this.createThreeDSContext(
      response.result.threeds_data,
      undefined,
    );
    if (!threeDSCtx) {
      return this.handleError(response.requestId);
    }
    // open popup or in-app-browser
    await this.initThreeDSwindow(threeDSCtx, response, depositReference);

    await this.getPostThreeDSProgressPromise(
      threeDSCtx.invoiceId,
      threeDSCtx.type,
    );

    const threeDsData = this.constructThreeDSResponseApiData(threeDSCtx);
    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    response = await this.authorization(
      depositMethod,
      billingAgreementId,
      amount,
      depositCurrencyId,
      paymentCurrencyId,
      threatMetrixSessionId,
      trackingToken,
      cartId,
      creditCard?.gateway,
      billingData,
      ThreedsCapabilityApi.VER1,
      threeDsData,
      threeDSCtx.invoiceId,
    );
    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return;
    }
    this.handleThreeDSResponse(response, depositMethod);
  }

  /**
   * Handle all credit card deposit request including 3DS popup opening.
   *
   * @param confirmation Deposit confirmation payload for deposit API
   */
  async creditCardDeposit(
    confirmation: DepositRequestConfirmationApi,
    worldpayContext: DepositPageVarsWorldpayDdc | undefined,
  ): Promise<void> {
    // First request
    const depositReference = Date.now();
    this.depositReference = depositReference;
    const depositConfirmation = confirmation;
    let response = await this.depositUpdate(depositConfirmation);

    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return;
    }

    // Perform DDC for Worldpay
    if (
      response.result.threeds_data &&
      response.result.threeds_data.threeds_state === ThreeDSState.DO_DDC &&
      worldpayContext
    ) {
      let flnBillingDataObj;
      if (depositConfirmation.fln_billing_data) {
        flnBillingDataObj = JSON.parse(depositConfirmation.fln_billing_data);
      }
      if (!flnBillingDataObj) {
        this.handleError(
          response.requestId,
          PaymentsErrorType.ERROR_UNDEFINED_DATA,
        );
        return;
      }
      const bin = flnBillingDataObj.value.find(
        (arr: { name: string; value: any }) => arr.name === 'iin',
      );
      if (!bin) {
        this.handleError(
          response.requestId,
          PaymentsErrorType.ERROR_UNDEFINED_DATA,
        );
        return;
      }
      const cardinalCommerceSessionId = await this.initWorldpayDdc(
        worldpayContext,
        bin?.value,
      );
      const ddcSessionId = {
        name: 'ddcSessionId',
        value: cardinalCommerceSessionId,
      };
      flnBillingDataObj.value.push(ddcSessionId);
      const flnBlnData = JSON.stringify(flnBillingDataObj);
      const confirmationAfterDdc: DepositRequestConfirmationApi = {
        user_id: depositConfirmation.user_id,
        invoice_id: response.result.threeds_data.invoice_id,
        deposit_method: depositConfirmation.deposit_method,
        fln_billing_data: flnBlnData,
        credit_card_gateway: response.result?.threeds_data?.credit_card_gateway,
        cart_id: depositConfirmation.cart_id,
        threeds_capability: depositConfirmation.threeds_capability,
        tracking_token: depositConfirmation.tracking_token,
        action: depositConfirmation.action,
        may_require_mandate: depositConfirmation.may_require_mandate,
      };
      response = await this.depositUpdate(confirmationAfterDdc);
      if (response.status !== 'success') {
        this.handleError(response.requestId, response.errorCode);
        return;
      }
    }
    if (this.handleThreeDSResponse(response, confirmation.deposit_method)) {
      return;
    }

    // 3DS is triggered
    if (!response.result.threeds_data) {
      return this.handleError(response.requestId);
    }

    const threeDSCtx = this.createThreeDSContext(
      response.result.threeds_data,
      undefined,
    );

    if (!threeDSCtx) {
      return this.handleError(response.requestId);
    }
    // open popup or in-app-browser
    await this.initThreeDSwindow(
      threeDSCtx,
      response,
      depositReference,
      confirmation,
    );
  }

  /**
   * Continue credit card 3DS deposit flow when popup overlay CTA is clicked.
   */
  async continueThreeDSCreditCardDeposit(
    response: ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >,
    threeDSCtx: ThreeDSContext | undefined,
    depositReference: number,
    depositConfirmation?: DepositRequestConfirmationApi,
  ): Promise<void> {
    let depositUpdateResponse = response;
    let continuethreeDSCtx = threeDSCtx;
    if (!continuethreeDSCtx) {
      return this.handleError(depositUpdateResponse.requestId);
    }

    if (!depositConfirmation) {
      return this.handleError(depositUpdateResponse.requestId);
    }

    let depositConfirmationRequest = await this.generateConfirmationRequest(
      continuethreeDSCtx,
      depositConfirmation,
    );

    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    depositUpdateResponse = await this.depositUpdate(
      depositConfirmationRequest,
    );

    // 3DS is in progress
    // Received an error response from API
    if (depositUpdateResponse.status !== 'success') {
      this.handleError(
        depositUpdateResponse.requestId,
        depositUpdateResponse.errorCode,
      );
      return;
    }

    if (
      this.handleThreeDSResponse(
        depositUpdateResponse,
        depositConfirmation?.deposit_method,
        true,
      )
    ) {
      return;
    }

    // 3DS is triggered
    if (!depositUpdateResponse.result.threeds_data) {
      return this.handleError(depositUpdateResponse.requestId);
    }

    continuethreeDSCtx = this.createThreeDSContext(
      depositUpdateResponse.result.threeds_data,
      continuethreeDSCtx,
    );
    if (!continuethreeDSCtx) {
      return this.handleError(depositUpdateResponse.requestId);
    }
    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    depositConfirmationRequest = await this.generateConfirmationRequest(
      continuethreeDSCtx,
      depositConfirmationRequest,
    );
    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    depositUpdateResponse = await this.depositUpdate(
      depositConfirmationRequest,
    );
    if (depositUpdateResponse.status !== 'success') {
      this.handleError(
        depositUpdateResponse.requestId,
        depositUpdateResponse.errorCode,
      );
      return;
    }
    this.handleThreeDSResponse(
      depositUpdateResponse,
      depositConfirmation?.deposit_method,
      true,
    );
  }

  /**
   * initialise the popup window for webapp or in app browser for Capacitor App.
   */
  async initThreeDSwindow(
    threeDSCtx: ThreeDSContext,
    response: ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >,
    depositReference: number,
    depositConfirmation?: DepositRequestConfirmationApi,
  ): Promise<void> {
    this.tracking.trackCustomEvent('ThreeDSRequired');
    if (this.pwa.isNative()) {
      // First time 3DS triggered
      if (depositConfirmation) {
        this.continueThreeDSCreditCardDeposit(
          response,
          threeDSCtx,
          depositReference,
          depositConfirmation,
        );
      } else {
        // Recurring 3DS triggered
        this.initiate3DSFlow(threeDSCtx);
      }
    }
    this.paymentsMessaging.pushOverlay({
      brand: {
        src: this.paymentsUtils.getMethodLogoPath(DepositMethodApi.WIRE),
        alt: 'Bank Logo',
      },
      ctaAction: async () => {
        // If popup has been opened before don't initiate
        if (this.threeDSPopUp) {
          const threeDSPopUp =
            await this.popUpService.openPopUp('threeDSPopup');
          threeDSPopUp.focus();
          return;
        }

        const threeDSPopUp = await this.popUpService.openPopUp('threeDSPopup');
        if (threeDSPopUp) {
          this.threeDSPopUp = threeDSPopUp;
          // First time 3DS triggered
          if (depositConfirmation) {
            await this.continueThreeDSCreditCardDeposit(
              response,
              threeDSCtx,
              depositReference,
              depositConfirmation,
            );
          } else {
            // Recurring 3DS triggered
            this.initiate3DSFlow(threeDSCtx);
          }
        } else {
          return this.paymentsMessaging.pushError({
            errorType: PaymentsErrorType.ERROR,
            errorMessage: 'Failed to open popup window',
          });
        }
      },
      onClose: () => this.cancelThreeDSFlow(),
      overlayType: OverlayTypes.CC_3DS,
    });
  }

  /**
   * generate new confirmation request payload based on the post message
   *
   * @param threeDSCtx 3DS context data object
   * @param confirmation Deposit confirmation payload for deposit API
   */
  async generateConfirmationRequest(
    threeDSCtx: ThreeDSContext,
    confirmation: DepositRequestConfirmationApi,
  ): Promise<DepositRequestConfirmationApi> {
    this.initiate3DSFlow(threeDSCtx);

    await this.getPostThreeDSProgressPromise(
      threeDSCtx.invoiceId,
      threeDSCtx.type,
    );

    return {
      user_id: confirmation.user_id,
      deposit_method: DepositMethodApi.FLN_BILLING,
      fln_billing_data: confirmation.fln_billing_data,
      credit_card_gateway: threeDSCtx.ccGateway,
      cart_id: confirmation.cart_id,
      threeds_capability: ThreedsCapabilityApi.VER2,
      invoice_id: threeDSCtx.invoiceId,
      tracking_token: confirmation.tracking_token,
      threeds_response_data: this.constructThreeDSResponseApiData(threeDSCtx),
      action: confirmation.action,
      may_require_mandate: confirmation.may_require_mandate,
    };
  }

  /**
   * Handle the response from credit card deposit API call.
   *
   * @param response Response from deposit API
   */
  private handleThreeDSResponse(
    response: ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >,
    method?: DepositMethodApi,
    isFrom3dsWindow = false,
  ): boolean {
    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return true;
    }
    if (!response.result.is_threeds_triggered) {
      if (this.browser && this.pwa.isNative()) {
        this.browser.close();
      } else {
        this.popUpService.disableOverlayCloseEvent();
        this.popUpService.closePopUp();
        this.threeDSPopUp = undefined;
      }
      // Deposit is successful.
      this.paymentsMessaging.pushResult({
        method,
        paymentsStatus: PaymentsResultStatus.SUCCESS,
        is3ds: isFrom3dsWindow,
      });
      return true;
    }
    // 3DS is triggered
    if (!response.result.threeds_data) {
      this.handleError(response.requestId);
      return true;
    }
    return false;
  }

  /**
   * Close overlay
   */
  private closeOverlay(): void {
    this.paymentsMessaging.pushEvent({
      eventType: PaymentsEventType.CLOSE_OVERLAY,
    });
  }

  /**
   * Handle the error response from API call
   *
   * @param eventId Event ID from API response
   * @param errorCode The error code from the API response
   * @param paymentMethod Payment method from API response
   */
  private handleError(
    eventId?: string,
    errorCode?: string,
    paymentMethod?: string,
  ): void {
    this.closeOverlay();
    if (this.pwa.isNative()) {
      this.browser.close();
    } else {
      this.popUpService.closePopUp();
      this.threeDSPopUp = undefined;
    }
    this.paymentsMessaging.pushError({
      errorCode,
      errorType: errorCode
        ? PaymentsErrorType.ERROR
        : PaymentsErrorType.ERROR_UNKNOWN,
      eventId,
      paymentMethod,
    });
  }

  /**
   * Get result post message from GAF
   */
  getThreeDSDepositResultPromise(): Promise<MessageEvent['data']> {
    return firstValueFrom(
      this.paymentsUtils
        .getFilteredMessageStream(
          event => !!event.data.threeDSFailure || !!event.data.threeDSSuccess,
        )
        .pipe(untilDestroyed(this)),
    );
  }

  /**
   * Get 3DS post message based on 3DS state.
   *
   * @param threeDSState 3DS state for different flows
   */
  private getPostThreeDSProgressPromise(
    invoiceId: number,
    threeDSState: string,
  ): Promise<any> {
    return firstValueFrom(
      merge(
        this.getPostThreeDSProgressFromWS(invoiceId, threeDSState),
        this.pwa.isNative()
          ? this.getPostThreeDSProgressFromIAB(threeDSState)
          : this.getPostThreeDSProgressFromPopUp(threeDSState),
      ).pipe(untilDestroyed(this)),
    );
  }

  /**
   * Get 3DS post message based on invoice ID and 3DS state from WebSocket.
   * Will be received by all platforms.
   */
  private getPostThreeDSProgressFromWS(
    invoiceId: number,
    threeDSState: string,
  ): Observable<WebsocketMessage<ThreeDSecureCompletedData>> {
    return this.webSocketService.fromServerStream$.pipe(
      filterWebsocketMessage<ThreeDSecureCompletedData>(),
      filter(
        message =>
          message.body.parent_type === 'notifications' &&
          message.body.type === 'threeDSecureCompleted' &&
          message.body.data.invoiceId === invoiceId &&
          message.body.data.threeDSState === threeDSState,
      ),
    );
  }

  /**
   * Get 3DS post message based on 3DS state from popup window.
   * Primarily used for desktop web.
   */
  private getPostThreeDSProgressFromPopUp(
    threeDSState: string,
  ): Observable<MessageEvent['data']> {
    return this.paymentsUtils.getFilteredMessageStream(
      event =>
        event.data.threeDSState === threeDSState &&
        (!!event.data.threeDSFailure ||
          (!!event.data.type && event.data.type === ThreeDSMessageType.RETURN)),
    );
  }

  /**
   * Get 3DS post message based on 3DS state from in-app-browser.
   * Primarily used for the Android and iOS native app.
   */
  private getPostThreeDSProgressFromIAB(
    threeDSState: string,
  ): Observable<InAppBrowserEvent> {
    return this.browser
      .onMessage()
      .pipe(
        filter(
          event =>
            !!event.data.threeDSFailure ||
            (event.data.type === ThreeDSMessageType.RETURN &&
              event.data.threeDSState === threeDSState),
        ),
      );
  }

  /**
   * Emit error message to show the banner in deposit page
   *
   * @param errorMessage Error message from API response
   * @param errorType Error type from API response
   */
  emitErrorMessage(
    errorMessage: string,
    errorType:
      | PaymentsErrorType.ERROR
      | PaymentsErrorType.ERROR_AUTH
      | PaymentsErrorType.ERROR_INIT
      | PaymentsErrorType.ERROR_UNKNOWN,
  ): void {
    this.paymentsMessaging.pushError({
      errorMessage,
      errorType,
    });
  }

  /**
   * Cancel the 3DS flow
   */
  cancelThreeDSFlow(): void {
    if (this.pwa.isNative()) {
      this.browser.close();
    } else {
      this.popUpService.closePopUp();
      this.threeDSPopUp = undefined;
    }
    this.paymentsMessaging.pushResult({
      paymentsStatus: PaymentsResultStatus.INTERRUPTED,
    });
  }

  /**
   * setup popup window for third part payment methods.
   * @param popup Window object for 3DS popup
   * @param response Deposit API response
   * @param method
   * @param cartId
   */
  private setupRedirectionAndListen(
    popup: Window,
    response: SuccessResponseData<DepositRequestActionApi>,
    method: DepositMethodApi,
    cartId?: number,
  ): Subscription | undefined {
    const { url } = response.result;
    if (!url) {
      this.paymentsMessaging.pushError({ errorType: PaymentsErrorType.ERROR });
      return undefined;
    }

    // redirect and listen for deposit result
    popup.location.href = url;
    const depositSubscription = this.paymentsUtils
      .listenForDepositResult()
      .subscribe(data => {
        depositSubscription.unsubscribe();
        this.popUpService.closePopUp();
        this.paymentPopUp = undefined;
        const message = this.paymentsUtils.handleInterframeDepositResult(data);
        if (message) {
          if (
            'paymentsStatus' in message &&
            message.paymentsStatus === PaymentsResultStatus.PENDING &&
            (response.result.deposit_method ===
              DepositMethodApi.PAYPAL_REFERENCE ||
              response.result.deposit_method === DepositMethodApi.PAYPAL)
          ) {
            this.popUpService.disableOverlayCloseEvent();
            this.paymentsMessaging.pushOverlay({
              titleText: $localize`Checkout Complete`,
              overlayType: OverlayTypes.PAYPAL_PENDING,
              cartId,
            });
          }
          const messageWithMethod = {
            ...message,
            method,
          };
          this.paymentsMessaging.pushMessage(messageWithMethod);
        } else {
          this.handleError(response.requestId);
        }
      });
    return depositSubscription;
  }

  processUpgradesAndMilestones(
    requestData: PreprocessMilestonesRequest,
  ): Promise<PreprocessMilestonesResponse | undefined> {
    return firstValueFrom(
      this.freelancerHttp
        .post<PreprocessMilestonesResponse>(
          `payment/on-preprocess-upgrades-and-milestones.php`,
          requestData,
          {
            isGaf: true,
            serializeBody: true,
          },
        )
        .pipe(
          map(response =>
            response.status === 'success' ? response.result : undefined,
          ),
          untilDestroyed(this),
        ),
    );
  }

  /**
   * Temporary function to check if use Capacitor App.
   */
  isNative(): boolean {
    return this.pwa.isNative();
  }

  checkIfCaptchaRequired(url: string): Promise<boolean> {
    return firstValueFrom(
      this.http.get(url, { responseType: 'text' }).pipe(untilDestroyed(this)),
    )
      .then(_ => false)
      .catch(
        e =>
          // Means it is a CORS error, which shall be caused by the captcha
          e.status === 0,
      );
  }

  getWorldPayDdcResponseStream(url: string): Observable<MessageEvent['data']> {
    return fromEvent<MessageEvent['data']>(window, 'message').pipe(
      filter(m => m.origin === url),
      map(m => m.data),
    );
  }

  getWorldPayDdcPromise(url: string): Promise<MessageEvent['data']> {
    return firstValueFrom(
      this.paymentsUtils
        .getExternalServiceMessageStream(url, event => event.data !== undefined)
        .pipe(timeout(15_000), untilDestroyed(this)),
    );
  }

  getWorldPayFormField(targetId: string): HTMLElement | undefined {
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      return targetElement;
    }
  }

  /**
   * Initiate worldpay device data collection process
   */
  async initWorldpayDdc(
    worldpayContext: DepositPageVarsWorldpayDdc,
    bin: string,
  ): Promise<string> {
    const cardinalCommercePostMessage = this.getWorldPayDdcPromise(
      worldpayContext.originUrl,
    );

    const formBin = this.getWorldPayFormField('formBin');
    formBin?.setAttribute('value', bin);
    const formJwt = this.getWorldPayFormField('formJwt');
    formJwt?.setAttribute('value', worldpayContext.jwt);
    const ddcForm = this.getWorldPayFormField('ddcForm') as HTMLFormElement;
    ddcForm?.setAttribute('action', worldpayContext.url);
    ddcForm.submit();

    try {
      const cardinalCommercePostMessageObject = JSON.parse(
        await cardinalCommercePostMessage,
      );
      let cardinalCommerceSessionId = '';
      if (cardinalCommercePostMessageObject.Status) {
        cardinalCommerceSessionId = cardinalCommercePostMessageObject.SessionId;
      }
      return cardinalCommerceSessionId;
    } catch (e) {
      console.error(`DDC error: ${e}`);
      return '';
    }
  }

  getDepositPageContext(): DatastoreCollection<DepositPageVarsCollection> {
    const queryParam = (param: keyof Params): Observable<string> =>
      this.activatedRoute.queryParams.pipe(map(v => v[param]));
    return this.datastore.collection<DepositPageVarsCollection>(
      'depositPageVars',
      query =>
        query
          .where('amount', '==', queryParam('amount').pipe(map(toNumber)))
          .where('back', '==', queryParam('back'))
          .where('orgBackUrl', '==', queryParam('backUrl'))
          .where('bidderId', '==', queryParam('bidderId'))
          .where('contractQuantity', '==', queryParam('contract_quantity'))
          .where('currency', '==', queryParam('currency').pipe(map(toNumber)))
          .where('dataToken', '==', queryParam('dataToken'))
          .where('descr', '==', queryParam('descr'))
          .where('durationCycle', '==', queryParam('duration_cycle'))
          .where('durationType', '==', queryParam('duration_type'))
          .where('entryId', '==', queryParam('entry_id'))
          .where(
            'hiremeInitialMilestoneTracking',
            '==',
            queryParam('hireme_initial_milestone_tracking'),
          )
          .where('pid', '==', queryParam('id')) // transform id to pid
          .where('listingId', '==', queryParam('listing_id'))
          .where('membershipCoupon', '==', queryParam('membership_coupon'))
          .where('membershipRef', '==', queryParam('membership_ref'))
          .where('milestoneAmount', '==', queryParam('milestone_amount'))
          .where('milestoneRequestId', '==', queryParam('milestone_request_id'))
          .where('nextAction', '==', queryParam('nextAction'))
          .where('oneTimeNotify', '==', queryParam('one_time_notify'))
          .where('packageId', '==', queryParam('package_id'))
          .where('priorityRef', '==', queryParam('priority_ref'))
          .where('redirected', '==', queryParam('redirected'))
          .where('refAction', '==', queryParam('refAction'))
          .where('returnToProject', '==', queryParam('return_to_project'))
          .where(
            'toCorporateOnboarding',
            '==',
            queryParam('to_corporate_onboarding'),
          )
          .where(
            'toFinancialDashboard',
            '==',
            queryParam('to_financial_dashboard'),
          )
          .where('orgTrackingToken', '==', queryParam('trackingToken'))
          .where('uid', '==', queryParam('uid'))
          .where('upgradeOptions', '==', queryParam('upgrade_options'))
          .where('username', '==', queryParam('username'))
          .where('verifiedPayment', '==', queryParam('verified_payment'))
          .where('paymentItem', '==', queryParam('paymentItem'))
          .where('cartId', '==', queryParam('cartId').pipe(map(toNumber))),
    );
  }

  private async nativePayPalCheckoutAuth(
    url: string,
    cartId?: number,
    requestId?: string,
  ): Promise<void> {
    let result;

    try {
      result = await this.paypalNativeCheckoutAuth.startNativeCheckoutAuth(url);
    } catch (e) {
      // platform or app version isn't supported
      this.handlePayPalNativeCheckoutError(
        requestId,
        e instanceof Error ? e.message : undefined,
      );
      return;
    }

    if (result === undefined) {
      // User cancelled the checkout flow
      this.paymentsMessaging.pushEvent({
        eventType: PaymentsEventType.CLOSE_OVERLAY,
      });

      return;
    }

    const message = this.paymentsUtils.handleInterframeDepositResult(result);
    if (message) {
      if (
        'paymentsStatus' in message &&
        message.paymentsStatus === PaymentsResultStatus.PENDING
      ) {
        this.paymentsMessaging.pushOverlay({
          titleText: $localize`Checkout Complete`,
          overlayType: OverlayTypes.PAYPAL_PENDING,
          cartId,
        });
      }
      this.paymentsMessaging.pushMessage(message);
    } else {
      this.handlePayPalNativeCheckoutError(requestId);
    }
  }

  private handlePayPalNativeCheckoutError(
    eventId?: string,
    errorMessage?: string,
  ): void {
    this.closeOverlay();
    this.paymentsMessaging.pushError({
      errorType: PaymentsErrorType.ERROR_UNKNOWN,
      errorMessage,
      eventId,
      paymentMethod: DepositMethodApi.PAYPAL,
    });
  }

  private isUserEligibleForNewNativePayPalCheckout(): boolean {
    return this.paypalNativeCheckoutAuth.canUseNativeCheckoutAuth();
  }
}
